// List of USD-using locales for currency determination
export const USD_LOCALES = ['en-US', 'en-PR', 'es-PR', 'es-US', 'es-MX', 'en-PH'];

// List of explicit EUR-using locales and countries
export const EUR_LOCALES = [
  // Countries in the Eurozone with their primary locales
  'de-DE', 'fr-FR', 'it-IT', 'es-ES', 'pt-PT', 'nl-NL', 
  'el-GR', 'fi-FI', 'sk-SK', 'sl-SI', 'et-EE', 'lv-LV', 
  'lt-LT', 'ga-IE', 'mt-MT', 'cy-MT', 'sv-FI', 'da-DK',
  // Any locale specifically marked for Europe
  'en-GB', 'en-IE', 'en-EU'
];

// Two-letter country codes for Eurozone countries 
export const EUR_COUNTRIES = [
  'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI',
  'ES', 'HR', 'BG', 'CZ', 'DK', 'HU', 'PL', 'RO', 'SE',
  // Include non-EU European countries that often use EUR
  'CH', 'NO'
];

export type CurrencyType = "EUR" | "USD";
export type PlanIdType = "free" | "pro";

// Feature type including tooltip description
export type FeatureWithTooltip = {
  text: string;
  tooltip: string;
};

/**
 * Advanced detection of user's currency with improved European detection
 * Prioritizes:
 * 1. Timezone information (most reliable for location)
 * 2. Navigator language with country code
 * 3. IP-based location detection (if navigator.language not available)
 * 4. Falls back to EUR for European focused product
 */
export const detectUserCurrency = (): CurrencyType => {
  try {
    if (typeof navigator !== 'undefined') {
      // 1. Try to get TimeZone-based location (most reliable indicator)
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timeZone) {
        // European time zones generally contain Europe/ or have country codes from Europe
        if (timeZone.includes('Europe/') || EUR_COUNTRIES.some(country => timeZone.includes(`/${country}`))) {
          return "EUR";
        }
      }

      // 2. Check navigator languages (all languages, not just preferred)
      if (navigator.languages && navigator.languages.length > 0) {
        // Check if any language in the list is from a EUR country
        for (const lang of navigator.languages) {
          // Check full locale match first
          if (EUR_LOCALES.includes(lang)) {
            return "EUR";
          }
          if (USD_LOCALES.includes(lang)) {
            return "USD";
          }
          
          // Check country code from locale
          const parts = lang.split('-');
          if (parts.length > 1) {
            const countryCode = parts[1].toUpperCase();
            if (EUR_COUNTRIES.includes(countryCode)) {
              return "EUR";
            }
          }
        }
      }
      
      // 3. Fall back to primary language if languages array not available
      if (navigator.language) {
        const locale = navigator.language;
        
        // Explicit matches first
        if (EUR_LOCALES.includes(locale)) {
          return "EUR";
        }
        
        if (USD_LOCALES.includes(locale)) {
          return "USD";
        }
        
        // Check country code from locale (en-GB, en-IE, etc)
        const parts = locale.split('-');
        if (parts.length > 1) {
          const countryCode = parts[1].toUpperCase();
          if (EUR_COUNTRIES.includes(countryCode)) {
            return "EUR";
          }
        }
      }
    }
    
    // 4. Default to EUR for European focus
    return "EUR";
  } catch (error) {
    console.error("Error detecting locale:", error);
    return "EUR"; // Fallback to EUR if detection fails
  }
};

// For testing - can be used to override detection in development
export const forceCurrency = (currency: CurrencyType): void => {
  localStorage.setItem('preferredCurrency', currency);
};

/**
 * Gets user's currency preference with the following priority:
 * 1. User explicitly saved preference (from localStorage)
 * 2. Detected currency based on timezone, locale and country
 */
export const getUserCurrency = (): CurrencyType => {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('preferredCurrency') as CurrencyType;
    if (saved === 'EUR' || saved === 'USD') {
      return saved;
    }
  }
  return detectUserCurrency();
};

// Save user's currency preference
export const saveCurrencyPreference = (currency: CurrencyType): void => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('preferredCurrency', currency);
  }
};

// Get currency symbol for display
export const getCurrencySymbol = (currency: CurrencyType): string => 
  currency === "EUR" ? "€" : "$";

// Format price for display
export const formatPrice = (planId: string, currency: CurrencyType, type: string = 'monthly'): string => {
  const symbol = getCurrencySymbol(currency);
  
  const priceMap: Record<string, { EUR: number; USD: number }> = {
    free: { EUR: 0, USD: 0 },
    pro: { EUR: 29, USD: 29 },
  };

  const priceData = priceMap[planId] || priceMap['pro'];
  return `${symbol}${priceData[currency]}`;
};

// Get numeric price for calculations
export const getPlanPrice = (planId: string, currency: CurrencyType, type: string = 'monthly'): number => {
  const priceMap: Record<string, { EUR: number; USD: number }> = {
    free: { EUR: 0, USD: 0 },
    pro: { EUR: 29, USD: 29 },
  };

  const priceData = priceMap[planId] || priceMap['pro'];
  return priceData[currency];
};

// Pricing data with proper currency support
export const PRICING_TIERS = [
  {
    id: "free" as PlanIdType,
    name: "Free",
    description: "Try onboarding and analysis without paying.",
    prices: {
      EUR: 0,
      USD: 0
    },
    features: [
      { text: "Onboarding wizard", tooltip: "Complete setup and website analysis." },
      { text: "Preview analysis results", tooltip: "See your insights before subscribing." },
    ],
    cta: "Start Free",
    popular: false,
    highlight: false,
  },
  {
    id: "pro" as PlanIdType,
    name: "Pro",
    description: "Full access to LaunchGuide.",
    subtitle: "Everything unlocked:",
    prices: {
      EUR: 29,
      USD: 29
    },
    features: [
      { text: "All playbooks", tooltip: "Every phase, every task, no limits." },
      { text: "AI tools and agents", tooltip: "Analyze, research, competitor insights, Reddit agents." },
      { text: "Content and research", tooltip: "Topics, content library, vault, directories, PH launcher." },
      { text: "Ongoing updates", tooltip: "New tools and improvements included." },
    ],
    cta: "Subscribe",
    popular: true,
    highlight: true,
  },
]; 